import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UsesCommonCellProps {
  items: string[];
  maxVisible?: number;
}

/**
 * R8-F-003: +N 클릭 시 inline expand (tooltip popover 제거).
 * - 접힘 상태: maxVisible 개 chip + "+N 더보기" 버튼
 * - 펼침 상태: 전체 chip + "접기" 버튼 (중복 chip 없음)
 */
export function UsesCommonCell({ items, maxVisible = 2 }: UsesCommonCellProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const visible = expanded ? items : items.slice(0, maxVisible);
  const remaining = items.length - maxVisible;
  const hasMore = remaining > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--spacing-1)',
        alignItems: 'center',
      }}
    >
      {visible.map((item) => (
        <span
          key={item}
          style={{
            display: 'inline-block',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-mono)',
            whiteSpace: 'nowrap',
          }}
        >
          {item}
        </span>
      ))}

      {/* +N expand 버튼 (접힘 상태에서만 표시) */}
      {!expanded && hasMore && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={t('index.usesCommon.expandAria', { count: remaining })}
          aria-expanded={false}
          style={{
            display: 'inline-block',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            border: 'none',
            lineHeight: '1.4',
          }}
        >
          +{remaining}
        </button>
      )}

      {/* 접기 버튼 (펼침 상태에서만 표시) */}
      {expanded && hasMore && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-label={t('index.usesCommon.collapseAria')}
          aria-expanded={true}
          style={{
            display: 'inline-block',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            borderRadius: 'var(--radius-full)',
            background: 'none',
            border: '1px solid var(--color-brand-primary)',
            color: 'var(--color-brand-primary)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            lineHeight: '1.4',
          }}
        >
          {t('index.usesCommon.collapse')}
        </button>
      )}
    </div>
  );
}
